<script>
import _ from "lodash";
import moment from "moment";
import Multiselect from "vue-multiselect";

import navigation from "../global/navigation.vue";
import archiveProofModal from "../modals/modal-archive-proof.vue";
import { PROOF_ACCESS_PERMISSIONS } from "../../utils/constants";

const { EVERYTHING } = PROOF_ACCESS_PERMISSIONS;

const STATUS_OPTIONS = [
  { value: 0, label: "Any status" },
  { value: 1, label: "Waiting for Review" },
  { value: 4, label: "Not Approved" },
  { value: 6, label: "Approved With Changes" },
  { value: 5, label: "Approved" },
];
const STATUS_LABEL_BY_VALUE = STATUS_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

// Sub-filter of "Waiting for Review": has an approver on the current stage opened the current version?
const VIEWED_OPTIONS = [
  { value: null, label: "Viewed or not" },
  { value: false, label: "Not yet viewed" },
  { value: true, label: "Viewed" },
];

const SORT_OPTIONS = [
  { value: "recent", label: "Most recent" },
  { value: "deadline", label: "Upcoming deadline" },
];

function emptyFilters() {
  return {
    status: 0,
    viewed: null,
    overdue: false,
    senderId: null,
    approverId: null,
    teamId: null,
    workflowId: null,
    tagIds: [],
    tagFilterIds: [],
    customFieldValues: [],
  };
}

export default {
  name: "ProofArchiveList",
  components: { navigation, Multiselect, archiveProofModal },
  data() {
    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem("experience-banner::proof-list::dismissed") === "1";
    } catch (_e) { /* ignore */ }
    return {
      bannerDismissed: dismissed,
      user: this.$A.Auth,
      isLoading: true,
      isFetching: false,
      isLoadingMore: false,
      hasMore: true,
      pageOn: 1,
      proofs: [],
      proofStatusColors: {},
      expandedIds: {},
      selectedIds: [],
      search: "",
      appliedSearch: "",
      viewState: "active",
      sortBy: "recent",
      sortMenuOpen: false,
      exportMenuOpen: false,
      sidebarCollapsed: false,
      appliedFilters: emptyFilters(),
      senders: [],
      contacts: [],
      contactsById: null,
      teams: [],
      workflows: [],
      customProperties: [],
      tags: [],
      tagFilters: [],
      pendingArchive: null,
      pendingRestore: null,
      pendingDelete: null,
      isExportingAll: false,
      isBulkBusy: false,
      statusOptions: STATUS_OPTIONS,
      viewedOptions: VIEWED_OPTIONS,
      sortOptions: SORT_OPTIONS,
    };
  },
  computed: {
    isArchivedView() {
      return this.viewState === "archived";
    },
    canManage() {
      return _.get(this, "user.permissions.proofAccess") === EVERYTHING;
    },
    canAdminAccount() {
      return !!_.get(this, "user.permissions.manageAccount");
    },
    selectedSet() {
      return new Set(this.selectedIds);
    },
    hasSelection() {
      return this.selectedIds.length > 0;
    },
    allVisibleSelected() {
      if (this.proofs.length === 0) return false;
      return this.proofs.every((p) => this.selectedSet.has(p.id));
    },
    canSelectAllPage() {
      return !this.allVisibleSelected && this.proofs.length > 0;
    },
    bulkCountWord() {
      return this.selectedIds.length === 1 ? "proof" : "proofs";
    },
    senderOptions() {
      return [
        { value: null, label: "Any owner" },
        ...this.senders.map((s) => ({ value: s.id, label: s.name })),
      ];
    },
    contactIds() {
      return this.contacts.map((c) => c.id);
    },
    teamOptions() {
      return [
        { value: null, label: "Any team" },
        ...this.teams.map((t) => ({ value: t.id, label: t.name })),
      ];
    },
    workflowOptions() {
      return [
        { value: null, label: "Any workflow" },
        ...this.workflows.map((w) => ({ value: w.id, label: w.name })),
      ];
    },
    activeFilters() {
      const f = this.appliedFilters;
      const items = [];
      if (this.viewState === "archived") {
        items.push({ key: "viewState", label: "View", value: "Archived" });
      }
      if (f.status && f.status !== 0) {
        items.push({
          key: "status",
          label: "Status",
          value: STATUS_LABEL_BY_VALUE[f.status] || String(f.status),
        });
      }
      if (f.status === 1 && typeof f.viewed === "boolean") {
        items.push({ key: "viewed", label: "Viewed", value: f.viewed ? "Viewed" : "Not yet viewed" });
      }
      if (f.overdue) {
        items.push({ key: "overdue", label: "Overdue", value: "Yes" });
      }
      if (f.senderId) {
        const s = this.senders.find((x) => x.id === f.senderId);
        if (s) items.push({ key: "senderId", label: "Owner", value: s.name });
      }
      if (f.approverId) {
        items.push({ key: "approverId", label: "Contact", value: this.contactLabel(f.approverId) });
      }
      if (f.teamId) {
        const t = this.teams.find((x) => x.id === f.teamId);
        if (t) items.push({ key: "teamId", label: "Team", value: t.name });
      }
      if (f.workflowId) {
        const w = this.workflows.find((x) => x.id === f.workflowId);
        if (w) items.push({ key: "workflowId", label: "Workflow", value: w.name });
      }
      (f.tagIds || []).forEach((id) => {
        const t = this.tags.find((x) => x.id === id);
        if (t) items.push({ key: `tagId:${id}`, label: "Tag", value: t.name, removeTagId: id });
      });
      (f.tagFilterIds || []).forEach((tf) => {
        const id = typeof tf === "object" && tf !== null ? tf.id : tf;
        const found = this.tagFilters.find((x) => x.id === id);
        if (found) items.push({ key: `tagFilterId:${id}`, label: "Tag filter", value: found.name, removeTagFilterId: id });
      });
      (f.customFieldValues || []).forEach((cv) => {
        const valueLabel = Array.isArray(cv.value) ? cv.value.join(", ") : cv.value;
        items.push({ key: `cf:${cv.key}`, label: cv.key, value: valueLabel, removeCustomFieldKey: cv.key });
      });
      return items;
    },
    hasActiveFilters() {
      return this.activeFilters.length > 0 || (this.appliedSearch && this.appliedSearch.length > 0);
    },
    hasAppliedSearch() {
      return !!(this.appliedSearch && this.appliedSearch.length);
    },
    hasAppliedFilterChips() {
      return this.activeFilters.length > 0;
    },
    noResultsHeadline() {
      if (this.hasAppliedSearch && this.hasAppliedFilterChips) return "Nothing fits all of that";
      if (this.hasAppliedSearch) return `No matches for “${this.appliedSearch}”`;
      return "No proofs match those filters";
    },
    noResultsSub() {
      if (this.hasAppliedSearch && this.hasAppliedFilterChips) {
        return "Loosen a filter or try a different search to widen the net.";
      }
      if (this.hasAppliedSearch) {
        return "Double-check spelling, or try a broader term.";
      }
      return "Adjust the filters in the sidebar — there might be a stricter one than you remember.";
    },
    loadedSummary() {
      if (this.isLoading) return "";
      if (this.proofs.length === 0) return "";
      if (this.hasMore) return `${this.proofs.length.toLocaleString()} loaded`;
      return `${this.proofs.length.toLocaleString()} ${this.proofs.length === 1 ? "proof" : "proofs"}`;
    },
  },
  created() {
    this._filtersReady = false;
    this._debouncedSearch = _.debounce(() => {
      this.appliedSearch = this.search.trim();
      this.fetchFirstPage();
    }, 220);
    this._debouncedFilterFetch = _.debounce(async () => {
      this.selectedIds = [];
      await this.fetchFirstPage();
      this.persistFilterCookie();
    }, 180);
    try {
      const stored = window.localStorage.getItem("proof::list::sidebar::collapsed");
      if (stored === "1") {
        this.sidebarCollapsed = true;
      } else if (stored === "0") {
        this.sidebarCollapsed = false;
      } else {
        // No saved preference — default to collapsed on mobile, open on desktop.
        this.sidebarCollapsed = window.innerWidth <= 720;
      }
    } catch (_e) { /* ignore */ }
  },
  async mounted() {
    document.addEventListener("click", this.handleDocClick);
    this.initSentinelObserver();
    this.applyViewToViewEntry();
    this.applyCachedFilter();
    // Load filter metadata and the first page of proofs concurrently. The
    // proof fetch only depends on the cached/view-to-view filter (applied
    // above), not on the metadata, so it must not be blocked behind it.
    await Promise.all([this.loadFilterData(), this.fetchFirstPage()]);
    this.isLoading = false;
    this.observeSentinel();
    this.$nextTick(() => { this._filtersReady = true; });
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleDocClick);
    if (this._sentinelObserver) this._sentinelObserver.disconnect();
  },
  watch: {
    "proofs.length"() {
      this.observeSentinel();
    },
    appliedFilters: {
      handler() {
        if (!this._filtersReady) return;
        this._debouncedFilterFetch();
      },
      deep: true,
    },
    "appliedFilters.status"(status) {
      if (status !== 1) this.appliedFilters.viewed = null;
    },
    viewState() {
      if (!this._filtersReady) return;
      this._debouncedFilterFetch();
    },
  },
  methods: {
    dismissBanner() {
      this.bannerDismissed = true;
      try {
        window.localStorage.setItem("experience-banner::proof-list::dismissed", "1");
      } catch (_e) { /* ignore */ }
    },
    formatDate(value) {
      if (!value) return "N/A";
      return moment(value).format("MMMM D, YYYY");
    },
    formatDateProp(prop) {
      if (prop.proofPropertyValue && prop.proofPropertyValue.length > 0) {
        return this.formatDate(prop.proofPropertyValue[0]);
      }
      return "N/A";
    },
    isAwaitingDecision(proof) {
      // statuses 4/5/6 are decided (not approved / approved / approved with changes)
      return !proof.archived && proof.status !== 4 && proof.status !== 5 && proof.status !== 6;
    },
    waitingOnSummary(proof) {
      const names = proof.waitingOn || [];
      if (!names.length) return "";
      const shown = names.slice(0, 2).join(", ");
      return names.length > 2 ? `${shown} +${names.length - 2} more` : shown;
    },
    viewProof(proof) {
      this.$A.NavigateToView("/proof-details", { proof });
    },
    proofItemLink(proof) {
      return `/proof-details?id=${proof.id}`;
    },
    handleViewProof(event, proof) {
      if (event.metaKey || event.ctrlKey) return;
      event.preventDefault();
      event.stopPropagation();
      this.viewProof(proof);
    },
    handleNameClick(event, proof) {
      event.stopPropagation();
      this.viewProof(proof);
    },
    toggleExpand(proofId) {
      this.$set(this.expandedIds, proofId, !this.expandedIds[proofId]);
    },
    isExpanded(proofId) {
      return !!this.expandedIds[proofId];
    },
    isSelected(id) {
      return this.selectedSet.has(id);
    },
    toggleSelect(id, event) {
      if (event) event.stopPropagation();
      const set = new Set(this.selectedIds);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      this.selectedIds = [...set];
    },
    selectAllOnPage() {
      const set = new Set(this.selectedIds);
      this.proofs.forEach((p) => set.add(p.id));
      this.selectedIds = [...set];
    },
    clearSelection() {
      this.selectedIds = [];
    },
    onSearchInput() {
      this._debouncedSearch();
    },
    clearSearchOnly() {
      this.search = "";
      this.appliedSearch = "";
      this.fetchFirstPage();
    },
    clearFiltersOnly() {
      this.appliedFilters = emptyFilters();
      this.viewState = "active";
    },
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      try {
        window.localStorage.setItem(
          "proof::list::sidebar::collapsed",
          this.sidebarCollapsed ? "1" : "0"
        );
      } catch (_e) { /* ignore */ }
    },
    async clearAllApplied() {
      this._filtersReady = false;
      this.appliedFilters = emptyFilters();
      this.search = "";
      this.appliedSearch = "";
      this.viewState = "active";
      this.sortBy = "recent";
      this.selectedIds = [];
      await this.fetchFirstPage();
      this.persistFilterCookie();
      this.$nextTick(() => { this._filtersReady = true; });
    },
    async removeFilter(chip) {
      this._filtersReady = false;
      const f = JSON.parse(JSON.stringify(this.appliedFilters));
      if (chip.removeTagId != null) {
        f.tagIds = (f.tagIds || []).filter((id) => id !== chip.removeTagId);
      } else if (chip.removeTagFilterId != null) {
        f.tagFilterIds = (f.tagFilterIds || []).filter((tf) => {
          const id = typeof tf === "object" && tf !== null ? tf.id : tf;
          return id !== chip.removeTagFilterId;
        });
      } else if (chip.removeCustomFieldKey != null) {
        f.customFieldValues = (f.customFieldValues || []).filter((cv) => cv.key !== chip.removeCustomFieldKey);
      } else if (chip.key === "viewState") {
        this.viewState = "active";
        this.selectedIds = [];
      } else if (chip.key === "status") {
        f.status = 0;
        f.viewed = null;
      } else if (chip.key === "viewed") {
        f.viewed = null;
      } else if (chip.key === "overdue") {
        f.overdue = false;
      } else if (chip.key === "senderId") {
        f.senderId = null;
      } else if (chip.key === "approverId") {
        f.approverId = null;
      } else if (chip.key === "teamId") {
        f.teamId = null;
      } else if (chip.key === "workflowId") {
        f.workflowId = null;
      }
      this.appliedFilters = f;
      await this.fetchFirstPage();
      this.persistFilterCookie();
      this.$nextTick(() => { this._filtersReady = true; });
    },
    getCustomValue(propertyLabel, dataType) {
      const cv = (this.appliedFilters.customFieldValues || []).find((c) => c.key === propertyLabel);
      if (!cv) return dataType === "Checkboxes (Multiple Options)" ? [] : "";
      if (dataType === "Checkboxes (Multiple Options)") return cv.value;
      return Array.isArray(cv.value) ? cv.value[0] : cv.value;
    },
    setCustomValue(property, value) {
      const rest = (this.appliedFilters.customFieldValues || []).filter((c) => c.key !== property.label);
      const isEmpty =
        value === "" ||
        value == null ||
        (Array.isArray(value) && value.length === 0);
      if (isEmpty) {
        this.$set(this.appliedFilters, "customFieldValues", rest);
        return;
      }
      let storedValue;
      if (property.dataType === "Checkboxes (Multiple Options)") {
        storedValue = value;
      } else if (property.dataType === "Datepicker") {
        try {
          storedValue = [this.$A.FormatDate(new Date(value))];
        } catch (_e) {
          storedValue = [value];
        }
      } else {
        storedValue = [value];
      }
      this.$set(this.appliedFilters, "customFieldValues", [
        ...rest,
        { key: property.label, value: storedValue, dataType: property.dataType },
      ]);
    },
    toggleCheckboxCustom(property, opt, checked) {
      const current = this.getCustomValue(property.label, property.dataType) || [];
      let next;
      if (checked) {
        next = current.includes(opt) ? current : [...current, opt];
      } else {
        next = current.filter((v) => v !== opt);
      }
      this.setCustomValue(property, next);
    },
    customCheckChecked(property, opt) {
      const val = this.getCustomValue(property.label, property.dataType) || [];
      return val.includes(opt);
    },
    contactLabel(id) {
      const c = this.contactsById
        ? this.contactsById.get(id)
        : this.contacts.find((x) => x.id === id);
      if (!c) return String(id);
      const name = c.name ? String(c.name) : "";
      const email = c.email ? String(c.email).toLowerCase() : "";
      if (name && email) return `${name} (${email})`;
      return name || email || String(id);
    },
    async loadFilterData() {
      try {
        const [senders, contacts, workflows, teams, customProperties, tags, tagFilters] = await Promise.all([
          this.$A.AccountService.All(),
          this.$A.ApproverService.AllIndexed(),
          this.$A.WorkflowService.GetAll(),
          this.$A.TeamService.GetAccountTeams(),
          this.$A.AccountService.GetAllCustomProofProperties(),
          this.$A.TagsService.GetAll(),
          this.$A.TagsService.GetAllTagFilters(),
        ]);
        this.senders = senders || [];
        // Accounts can have tens of thousands of contacts. Freeze the array so
        // Vue skips deep reactivity on every record (the main-thread cost), and
        // build an id->contact map so contactLabel() is O(1) instead of a 37k
        // scan per rendered multiselect option.
        const contactList = Array.isArray(contacts) ? contacts : [];
        this.contacts = Object.freeze(contactList);
        this.contactsById = new Map(contactList.map((c) => [c.id, c]));
        this.workflows = workflows || [];
        this.teams = teams || [];
        this.customProperties = _.isArray(customProperties) ? customProperties : [];
        this.tags = tags || [];
        this.tagFilters = tagFilters || [];
      } catch (e) {
        console.error("Failed to load filter data:", e);
      }
      const self = this;
      this.$root.$on("tags::reloaded", function (tags) {
        self.tags = tags;
      });
      this.$root.$on("tag::filters::reload", async function (tagFilters) {
        self.tagFilters = tagFilters;
      });
    },
    applyViewToViewEntry() {
      const v = this.$A.viewToViewData;
      if (v == null) return;
      if (v.tag != null && v.tag.id) {
        this.appliedFilters.tagIds = [v.tag.id];
      }
      if (v.tagFilter != null && v.tagFilter.id) {
        this.appliedFilters.tagFilterIds = [v.tagFilter.id];
      }
      if (v.filter != null && typeof v.filter.status === "number" && v.filter.status > -1) {
        const s = v.filter.status;
        if (s === 9) this.viewState = "archived";
        else if (s === 7) this.appliedFilters.overdue = true;
        else if (s === 8) this.sortBy = "deadline";
        else this.appliedFilters.status = s;
      }
    },
    applyCachedFilter() {
      const v = this.$A.viewToViewData;
      const fromEntry = v && (v.tag || v.tagFilter || (v.filter && typeof v.filter.status === "number"));
      if (fromEntry) return;
      try {
        const cached = this.$A.LoadCookie("proof::list::filter::update");
        if (!cached) return;
        const f = emptyFilters();
        if (cached.archived) this.viewState = "archived";
        if (cached.sortByDeadlineDesc) this.sortBy = "deadline";
        if (cached.overdue) f.overdue = true;
        if (typeof cached.status === "number") f.status = cached.status;
        if (typeof cached.viewedByApprover === "boolean") f.viewed = cached.viewedByApprover;
        if (cached.senderId) f.senderId = cached.senderId;
        if (cached.approverId) f.approverId = cached.approverId;
        if (cached.teamId) f.teamId = cached.teamId;
        if (cached.workflowId) f.workflowId = cached.workflowId;
        if (Array.isArray(cached.tagIds)) f.tagIds = cached.tagIds;
        if (Array.isArray(cached.tagFilterIds)) f.tagFilterIds = cached.tagFilterIds;
        if (Array.isArray(cached.customFieldValues)) f.customFieldValues = cached.customFieldValues;
        this.appliedFilters = f;
      } catch (_e) {
        // ignore
      }
    },
    persistFilterCookie() {
      try {
        this.$A.SaveCookie("proof::list::filter::update", this.apiFilter(), 1 / 48);
      } catch (_e) {
        // ignore
      }
    },
    apiFilter() {
      const f = this.appliedFilters;
      const api = {
        text: this.appliedSearch || "",
        status: typeof f.status === "number" ? f.status : 0,
        viewedByApprover: f.status === 1 && typeof f.viewed === "boolean" ? f.viewed : null,
        overdue: !!f.overdue,
        archived: this.viewState === "archived",
        sortByDeadlineDesc: this.sortBy === "deadline",
        senderId: f.senderId || 0,
        approverId: f.approverId || 0,
        teamId: f.teamId || 0,
        workflowId: f.workflowId || 0,
        tagIds: f.tagIds && f.tagIds.length ? f.tagIds : undefined,
        tagFilterIds: f.tagFilterIds && f.tagFilterIds.length ? f.tagFilterIds : undefined,
        tagFilterId: 0,
        customFieldValues: f.customFieldValues && f.customFieldValues.length ? f.customFieldValues : undefined,
      };
      return api;
    },
    async fetchFirstPage() {
      this.isFetching = true;
      this.pageOn = 1;
      this.hasMore = true;
      try {
        const proofs = await this.$A.ProofService.GetProofsByFilter(this.apiFilter(), 1);
        this.proofs = [];
        this.proofStatusColors = {};
        (proofs || []).forEach((p) => {
          p.checkbox = false;
          this.initProofStatusColor(p);
          this.proofs.push(p);
        });
        if (!proofs || proofs.length === 0) {
          this.hasMore = false;
        }
        this.selectedIds = [];
      } catch (e) {
        console.error("Failed to load proofs:", e);
      }
      this.isFetching = false;
    },
    async loadMore() {
      if (this.isLoadingMore || !this.hasMore || this.isFetching) return;
      this.isLoadingMore = true;
      const nextPage = this.pageOn + 1;
      try {
        const proofs = await this.$A.ProofService.GetProofsByFilter(this.apiFilter(), nextPage);
        if (!proofs || proofs.length === 0) {
          this.hasMore = false;
        } else {
          this.pageOn = nextPage;
          const seen = new Set(this.proofs.map((p) => p.id));
          proofs.forEach((p) => {
            if (seen.has(p.id)) return;
            p.checkbox = false;
            this.initProofStatusColor(p);
            this.proofs.push(p);
          });
        }
      } catch (e) {
        console.error("Failed to load more proofs:", e);
      }
      this.isLoadingMore = false;
    },
    initProofStatusColor(proof) {
      const meta = { status: "", color: "" };
      if (proof.status === 5) {
        meta.status = "Approved";
        meta.color = "background-color:#019256";
      } else if (proof.status === 6) {
        meta.status = "Approved With Changes";
        meta.color = "background-color:#019256";
      } else if (proof.status === 4) {
        meta.status = "Not Approved";
        meta.color = "background-color:#EB4654";
      } else if (proof.status === 2 || proof.status === 3) {
        meta.status = "Waiting for Review";
        meta.color = "background-color:#D9B116";
      } else if (proof.status === 1) {
        meta.status = "Created";
        meta.color = "background-color:#6B7280";
      } else {
        meta.status = "—";
        meta.color = "background-color:#6B7280";
      }
      this.$set(this.proofStatusColors, proof.id, meta);
    },
    initSentinelObserver() {
      if (this._sentinelObserver) this._sentinelObserver.disconnect();
      // Root must be the viewport, not .content-view: below 992px .page-body
      // is height:auto, so .content-view never scrolls (the document does) and
      // a non-clipping root reports the sentinel as always intersecting —
      // every observe() then fires loadMore() with no scrolling. A viewport
      // root works in both modes because ancestor clipping still applies when
      // .content-view is the scroller on desktop.
      this._sentinelObserver = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) this.loadMore();
          }
        },
        { root: null, rootMargin: "400px 0px", threshold: 0 }
      );
    },
    observeSentinel() {
      this.$nextTick(() => {
        const el = this.$refs.loadMoreSentinel;
        if (!el || !this._sentinelObserver) return;
        this._sentinelObserver.disconnect();
        this.initSentinelObserver();
        this._sentinelObserver.observe(el);
      });
    },
    openSingleArchive(proof) {
      this.pendingArchive = proof;
      this.$bvModal.show("archiveProofModal");
    },
    openSingleRestore(proof) {
      this.pendingRestore = proof;
      this.$bvModal.show("restoreProofModal");
    },
    openSingleDelete(proof) {
      this.pendingDelete = proof;
      this.$bvModal.show("deleteProofModal");
    },
    openManageTags() {
      this.$bvModal.show("ManageTags");
    },
    openManageTagFilters() {
      this.$bvModal.show("ManageTagFilters");
    },
    openBulkArchive() {
      this.pendingArchive = null;
      this.$bvModal.show("archiveProofModal");
    },
    openBulkRestore() {
      this.pendingRestore = null;
      this.$bvModal.show("restoreProofModal");
    },
    openBulkDelete() {
      this.pendingDelete = null;
      this.$bvModal.show("deleteProofModal");
    },
    async onArchiveConfirmed(args) {
      const note = args && args.note;
      const target = this.pendingArchive;
      this.pendingArchive = null;
      if (target) {
        try {
          await this.$A.ProofService.Archive(target.id, note);
          this.removeProofLocal(target.id);
        } catch (e) {
          console.error("Archive failed:", e);
        }
      } else {
        const ids = [...this.selectedIds];
        if (ids.length === 0) return;
        this.isBulkBusy = true;
        try {
          await this.$A.ProofService.ArchiveProofs(ids, note);
          this.selectedIds = [];
          await this.fetchFirstPage();
        } catch (e) {
          console.error("Bulk archive failed:", e);
        }
        this.isBulkBusy = false;
      }
    },
    async onRestoreConfirmed() {
      const target = this.pendingRestore;
      this.pendingRestore = null;
      if (target) {
        try {
          await this.$A.ProofService.Restore(target.id);
          this.removeProofLocal(target.id);
        } catch (e) {
          console.error("Restore failed:", e);
        }
      } else {
        const ids = [...this.selectedIds];
        if (ids.length === 0) return;
        this.isBulkBusy = true;
        try {
          await this.$A.ProofService.RestoreProofs(ids);
          this.selectedIds = [];
          await this.fetchFirstPage();
        } catch (e) {
          console.error("Bulk restore failed:", e);
        }
        this.isBulkBusy = false;
      }
    },
    async onDeleteConfirmed() {
      const target = this.pendingDelete;
      this.pendingDelete = null;
      if (target) {
        try {
          await this.$A.ProofService.Delete(target.id);
          this.removeProofLocal(target.id);
        } catch (e) {
          console.error("Delete failed:", e);
        }
      } else {
        const ids = [...this.selectedIds];
        if (ids.length === 0) return;
        this.isBulkBusy = true;
        try {
          await this.$A.ProofService.DeleteProofs(ids);
          this.selectedIds = [];
          await this.fetchFirstPage();
        } catch (e) {
          console.error("Bulk delete failed:", e);
        }
        this.isBulkBusy = false;
      }
    },
    removeProofLocal(id) {
      const idx = this.proofs.findIndex((p) => p.id === id);
      if (idx >= 0) this.proofs.splice(idx, 1);
      this.selectedIds = this.selectedIds.filter((x) => x !== id);
    },
    toggleExportMenu(event) {
      if (event) event.stopPropagation();
      this.exportMenuOpen = !this.exportMenuOpen;
      if (this.exportMenuOpen) this.sortMenuOpen = false;
    },
    closeExportMenu() {
      this.exportMenuOpen = false;
    },
    toggleSortMenu(event) {
      if (event) event.stopPropagation();
      this.sortMenuOpen = !this.sortMenuOpen;
      if (this.sortMenuOpen) this.exportMenuOpen = false;
    },
    closeSortMenu() {
      this.sortMenuOpen = false;
    },
    async setSort(value) {
      this.closeSortMenu();
      if (this.sortBy === value) return;
      this.sortBy = value;
      await this.fetchFirstPage();
      this.persistFilterCookie();
    },
    handleDocClick() {
      this.closeExportMenu();
      this.closeSortMenu();
    },
    async exportVisible() {
      this.closeExportMenu();
      const csv = this.generateProofsCsv(this.proofs);
      this.downloadCsv(csv, "visible");
    },
    async exportAll() {
      this.closeExportMenu();
      this.isExportingAll = true;
      try {
        const all = [];
        await this.$A.ProofService.GetProofsByFilterYieldAll({}, (results) => {
          (results || []).forEach((p) => {
            this.initProofStatusColor(p);
            all.push(p);
          });
        });
        if (all.length === 0) {
          this.$A.AlertUser("No proofs found to export", "warning");
        } else {
          this.downloadCsv(this.generateProofsCsv(all), "all");
        }
      } catch (e) {
        console.error("Export all failed:", e);
        this.$A.AlertUser("Error occurred while exporting proofs", "error");
      }
      this.isExportingAll = false;
    },
    generateProofsCsv(rows) {
      const baseHeaders = [
        "Proof ID", "Proof Name", "Status", "Current Version", "Created Date",
        "Version Created Date", "Owner", "Due Date", "Document Types", "Workflow",
      ];
      const customLabels = new Set();
      rows.forEach((p) => {
        if (Array.isArray(p.customFields)) {
          p.customFields.forEach((cf) => {
            if (cf.accountProofPropertyLabel) customLabels.add(cf.accountProofPropertyLabel);
          });
        }
      });
      const headers = [...baseHeaders, ...customLabels];
      const data = rows.map((p) => {
        const meta = this.proofStatusColors[p.id];
        const row = [
          p.id || "",
          p.name || "",
          meta ? meta.status : "",
          p.currentVersion || "",
          this.formatDate(p.createdAt),
          this.formatDate(p.currentVersionCreatedAt),
          p.currentSender || "",
          this.formatDate(p.dueDate),
          Array.isArray(p.docTypesUsed) ? p.docTypesUsed.join(", ") : "",
          p.workflowName || "",
        ];
        customLabels.forEach((label) => {
          const cf = (p.customFields || []).find((x) => x.accountProofPropertyLabel === label);
          if (cf && cf.proofPropertyValue) {
            row.push(Array.isArray(cf.proofPropertyValue) ? cf.proofPropertyValue.join("; ") : cf.proofPropertyValue);
          } else {
            row.push("");
          }
        });
        return row;
      });
      const lines = [headers, ...data].map((row) =>
        row
          .map((cell) => {
            const s = String(cell || "");
            if (s.includes(",") || s.includes('"') || s.includes("\n")) {
              return `"${s.replace(/"/g, '""')}"`;
            }
            return s;
          })
          .join(",")
      );
      return lines.join("\n");
    },
    downloadCsv(csv, kind) {
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const date = new Date().toISOString().split("T")[0];
      a.download = kind === "all" ? `proofs_export_all_${date}.csv` : `proofs_export_${date}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    },
  },
};
</script>

<template>
  <div class="nav-margin">
    <navigation />

    <div class="page-body">
    <div class="experience-switch" v-if="!bannerDismissed">
      <div class="experience-msg">
        <span class="experience-pill">New</span>
        <span class="experience-text">You're using the new proof list.</span>
      </div>
      <div class="experience-actions">
        <button type="button" class="experience-link experience-link-btn" v-b-modal="'feedbackModal-newProofList'">Give feedback</button>
        <span class="experience-divider" aria-hidden="true">·</span>
        <router-link to="/proof-archive" class="experience-link">Switch to classic →</router-link>
      </div>
      <button
        type="button"
        class="experience-close"
        @click="dismissBanner"
        aria-label="Dismiss banner"
        title="Dismiss"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>

    <feedbackModal
      name="feedbackModal-newProofList"
      campaign-key="new-proof-list-v1"
      title="How's the new proof list?"
      subtitle="Your take helps us decide what to keep."
      scale-question="Compared to the classic experience, how would you rate this?"
      :scale-min="1"
      :scale-max="7"
      scale-low-label="Much worse"
      scale-high-label="Much better"
      text-prompt="What's working or not working?"
      text-placeholder="Tell us anything that helps."
      submit-label="Send feedback"
    />

    <div class="proof-shell" :class="{ 'sidebar-collapsed': sidebarCollapsed }">

      <aside class="filter-rail" :aria-hidden="sidebarCollapsed ? 'true' : 'false'">
        <div class="rail-head" id="proofs_sort">
          <div class="rail-head-title">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            <span>Filters</span>
            <span v-if="activeFilters.length" class="rail-count">{{ activeFilters.length }}</span>
          </div>
          <button class="rail-collapse-btn" @click="toggleSidebar" aria-label="Collapse filters" title="Collapse">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        </div>

        <div class="rail-search">
          <img src="/img/search.svg" width="16" alt="" class="rail-search-icon" />
          <input
            type="text"
            class="rail-search-input"
            placeholder="Search proofs"
            v-model="search"
            @input="onSearchInput"
          />
        </div>

        <div class="rail-scroll">
          <div class="rail-group">
            <label class="rail-label">View</label>
            <div class="view-toggle" role="tablist" aria-label="Proof view">
              <button
                type="button"
                role="tab"
                class="view-toggle-btn"
                :class="{ 'is-active': viewState === 'active' }"
                :aria-selected="viewState === 'active' ? 'true' : 'false'"
                @click="viewState = 'active'"
              >Active</button>
              <button
                type="button"
                role="tab"
                class="view-toggle-btn"
                :class="{ 'is-active': viewState === 'archived' }"
                :aria-selected="viewState === 'archived' ? 'true' : 'false'"
                @click="viewState = 'archived'"
              >Archived</button>
            </div>
          </div>

          <div class="rail-group">
            <label class="rail-label">Status</label>
            <div class="select-wrap">
              <select class="filter-input filter-select" v-model.number="appliedFilters.status">
                <option v-for="o in statusOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
              </select>
              <svg class="select-caret" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="1 1 5 5 9 1" />
              </svg>
            </div>
            <div v-if="appliedFilters.status === 1" class="select-wrap viewed-select">
              <select class="filter-input filter-select" v-model="appliedFilters.viewed" aria-label="Viewed by an approver">
                <option v-for="o in viewedOptions" :key="String(o.value)" :value="o.value">{{ o.label }}</option>
              </select>
              <svg class="select-caret" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="1 1 5 5 9 1" />
              </svg>
            </div>
          </div>

          <div class="rail-group">
            <label class="overdue-toggle">
              <input type="checkbox" v-model="appliedFilters.overdue" />
              <span class="overdue-label">Only overdue proofs</span>
            </label>
          </div>

          <div class="rail-group">
            <label class="rail-label">Owner</label>
            <div class="select-wrap">
              <select class="filter-input filter-select" v-model="appliedFilters.senderId">
                <option v-for="o in senderOptions" :key="`s${o.value}`" :value="o.value">{{ o.label }}</option>
              </select>
              <svg class="select-caret" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="1 1 5 5 9 1" />
              </svg>
            </div>
          </div>

          <div class="rail-group">
            <label class="rail-label">Contact</label>
            <Multiselect
              v-model="appliedFilters.approverId"
              :options="contactIds"
              :multiple="false"
              :searchable="true"
              :close-on-select="true"
              :preserve-search="true"
              :custom-label="contactLabel"
              :placeholder="contacts.length ? 'Search contacts' : 'No contacts yet'"
            />
          </div>

          <div class="rail-group" v-if="teams.length">
            <label class="rail-label">Team</label>
            <div class="select-wrap">
              <select class="filter-input filter-select" v-model="appliedFilters.teamId">
                <option v-for="o in teamOptions" :key="`t${o.value}`" :value="o.value">{{ o.label }}</option>
              </select>
              <svg class="select-caret" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="1 1 5 5 9 1" />
              </svg>
            </div>
          </div>

          <div class="rail-group">
            <label class="rail-label">Workflow</label>
            <div class="select-wrap">
              <select class="filter-input filter-select" v-model="appliedFilters.workflowId">
                <option v-for="o in workflowOptions" :key="`w${o.value}`" :value="o.value">{{ o.label }}</option>
              </select>
              <svg class="select-caret" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="1 1 5 5 9 1" />
              </svg>
            </div>
          </div>

          <div class="rail-group" id="proofs_tags">
            <div class="rail-label-row">
              <label class="rail-label">Tags</label>
              <button type="button" class="manage-link" @click="openManageTags">Manage</button>
            </div>
            <Multiselect
              v-model="appliedFilters.tagIds"
              :options="tags.map(t => t.id)"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :custom-label="(id) => { const t = tags.find(x => x.id === id); return t ? t.name : String(id); }"
              :placeholder="tags.length ? 'Pick tags' : 'No tags yet'"
            />
          </div>

          <div class="rail-group">
            <div class="rail-label-row">
              <label class="rail-label">Tag groups</label>
              <button type="button" class="manage-link" @click="openManageTagFilters">Manage</button>
            </div>
            <Multiselect
              v-model="appliedFilters.tagFilterIds"
              :options="tagFilters.map(t => t.id)"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :preserve-search="true"
              :custom-label="(id) => { const t = tagFilters.find(x => x.id === id); return t ? t.name : String(id); }"
              :placeholder="tagFilters.length ? 'Pick saved tag groups' : 'No tag groups yet'"
            />
          </div>

          <template v-for="property in customProperties">
            <div class="rail-group" :key="`cp${property.label}`">
              <label class="rail-label">{{ property.label }}</label>

              <input
                v-if="property.dataType === 'Datepicker'"
                type="date"
                class="filter-input"
                :value="getCustomValue(property.label, property.dataType)"
                @change="setCustomValue(property, $event.target.value)"
              />

              <div v-else-if="property.dataType === 'Dropdown Select'" class="select-wrap">
                <select
                  class="filter-input filter-select"
                  :value="getCustomValue(property.label, property.dataType)"
                  @change="setCustomValue(property, $event.target.value)"
                >
                  <option value="">Any</option>
                  <option v-for="v in (property.enum || [])" :key="v" :value="v">{{ v }}</option>
                </select>
                <svg class="select-caret" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="1 1 5 5 9 1" />
                </svg>
              </div>

              <div v-else-if="property.dataType === 'Checkboxes (Multiple Options)'" class="check-grid">
                <label v-for="opt in (property.enum || [])" :key="opt" class="check-item">
                  <input
                    type="checkbox"
                    :checked="customCheckChecked(property, opt)"
                    @change="toggleCheckboxCustom(property, opt, $event.target.checked)"
                  />
                  <span>{{ opt }}</span>
                </label>
              </div>

              <input
                v-else
                type="text"
                class="filter-input"
                :value="getCustomValue(property.label, property.dataType)"
                @input="setCustomValue(property, $event.target.value)"
              />
            </div>
          </template>
        </div>

        <div class="rail-foot">
          <button
            class="rail-clear-btn"
            :disabled="!hasActiveFilters"
            @click="clearAllApplied"
          >Clear all filters</button>
        </div>
      </aside>

      <section class="proof-main-col">
        <div class="utility-bar" @click.stop>
          <div class="col-inner col-inner-row">
          <div class="utility-left">
            <button
              v-if="sidebarCollapsed"
              type="button"
              class="rail-expand-inline"
              @click="toggleSidebar"
              aria-label="Expand filters"
              title="Expand filters"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              <span>Filters</span>
              <span v-if="activeFilters.length" class="rail-expand-count">{{ activeFilters.length }}</span>
            </button>
            <span v-if="!isLoading" class="fs-12 text-gray-500 count-cell">
              <span v-if="isFetching" class="fetching-dot" aria-hidden="true"></span>
              {{ loadedSummary }}
            </span>
          </div>

          <div class="utility-right">
            <div class="export-wrap">
              <button class="tertiary-btn" :disabled="isExportingAll" @click="toggleExportMenu">
                <svg v-if="!isExportingAll" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                <span v-else class="export-spinner" aria-hidden="true"></span>
                Export
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <polyline points="1 1 5 5 9 1" />
                </svg>
              </button>
              <div v-if="exportMenuOpen" class="export-menu" role="menu">
                <button class="row-menu-item" role="menuitem" :disabled="proofs.length === 0" @click="exportVisible">
                  Export visible ({{ proofs.length }})
                </button>
                <button class="row-menu-item" role="menuitem" :disabled="isExportingAll" @click="exportAll">
                  Export all proofs
                </button>
              </div>
            </div>

            <div class="sort-wrap">
              <button class="tertiary-btn" :class="{ 'is-active-btn': sortBy !== 'recent' }" @click="toggleSortMenu">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="6" y1="12" x2="18" y2="12" />
                  <line x1="10" y1="18" x2="14" y2="18" />
                </svg>
                Sort
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <polyline points="1 1 5 5 9 1" />
                </svg>
              </button>
              <div v-if="sortMenuOpen" class="export-menu" role="menu">
                <button
                  v-for="opt in sortOptions"
                  :key="opt.value"
                  type="button"
                  class="row-menu-item"
                  :class="{ 'is-selected': sortBy === opt.value }"
                  role="menuitem"
                  @click="setSort(opt.value)"
                >
                  <svg v-if="sortBy === opt.value" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span v-else class="row-menu-check-spacer" aria-hidden="true"></span>
                  {{ opt.label }}
                </button>
              </div>
            </div>
          </div>
          </div>
        </div>

        <transition name="chip-bar">
          <div v-if="hasActiveFilters" class="chip-bar">
            <div class="col-inner col-inner-row">
            <div class="chip-scroll">
              <span v-for="f in activeFilters" :key="f.key" class="active-chip">
                <span class="chip-label">{{ f.label }}:</span>
                <span class="chip-value">{{ f.value }}</span>
                <button type="button" class="chip-close" :aria-label="`Remove ${f.label} filter`" @click="removeFilter(f)">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round">
                    <line x1="6" y1="6" x2="18" y2="18" />
                    <line x1="6" y1="18" x2="18" y2="6" />
                  </svg>
                </button>
              </span>
            </div>
            <button class="chip-clear-all" @click="clearAllApplied">Clear all</button>
            </div>
          </div>
        </transition>

        <div ref="scrollContainer" class="content-view">
          <div class="col-inner">
      <div v-if="isLoading" class="loading-state">Loading proofs...</div>

      <ul v-else-if="proofs.length" class="proof-rows list-unstyled mb-0">
        <li
          v-for="(proof, index) in proofs"
          :key="proof.id"
          :id="index === 0 ? 'proofs_item' : null"
          class="proof-row"
          :class="{ 'is-selected': isSelected(proof.id), 'is-expanded': isExpanded(proof.id) }"
        >
          <div class="proof-row-main" @click="toggleExpand(proof.id)">
            <label class="row-check-wrap" @click.stop>
              <input
                type="checkbox"
                class="row-checkbox"
                :checked="isSelected(proof.id)"
                @change="toggleSelect(proof.id, $event)"
              />
            </label>

            <div class="proof-main flex-fill min-w-0">
              <a
                class="proof-name"
                :href="proofItemLink(proof)"
                @click="handleNameClick($event, proof)"
              >
                {{ proof.name }}
              </a>
              <div class="proof-sub">
                <span class="status-badge">
                  <span class="status-dot" :style="proofStatusColors[proof.id] ? proofStatusColors[proof.id].color : ''"></span>
                  {{ proofStatusColors[proof.id] ? proofStatusColors[proof.id].status : "" }}
                </span>
                <span
                  v-if="(proof.status === 2 || proof.status === 3) && !proof.viewedByApprover"
                  class="status-badge not-viewed-badge"
                  title="No approver has opened this version yet"
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                  Not yet viewed
                </span>
                <span class="version-text">
                  Version {{ proof.currentVersion }} · Created {{ formatDate(proof.currentVersionCreatedAt) }}
                </span>
                <span
                  v-if="isAwaitingDecision(proof) && proof.currentStageName"
                  class="stage-text"
                  :title="(proof.waitingOn && proof.waitingOn.length) ? 'Waiting on ' + proof.waitingOn.join(', ') : ''"
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <polygon points="12 2 2 7 12 12 22 7 12 2" />
                    <polyline points="2 17 12 22 22 17" />
                    <polyline points="2 12 12 17 22 12" />
                  </svg>
                  {{ proof.currentStageName }}<span v-if="waitingOnSummary(proof)" class="stage-waiting"> · Waiting on <span class="stage-names">{{ waitingOnSummary(proof) }}</span></span>
                </span>
              </div>
            </div>

            <div class="proof-avatars">
              <span v-if="proof.docTypesUsed && (proof.docTypesUsed.indexOf('web_url') >= 0 || proof.docTypesUsed.indexOf('html') >= 0)" class="file-pill" :style="proofStatusColors[proof.id] ? proofStatusColors[proof.id].color : ''" title="Web">
                <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8m7.5-6.923c-.67.204-1.335.82-1.887 1.855A7.97 7.97 0 0 0 5.145 4H7.5zM4.09 4a9.27 9.27 0 0 1 .64-1.539 7 7 0 0 1 .597-.933A7.03 7.03 0 0 0 2.255 4zm-.582 3.5c.03-.877.138-1.718.312-2.5H1.674a7 7 0 0 0-.656 2.5zM4.847 5a12.5 12.5 0 0 0-.338 2.5H7.5V5zM8.5 5v2.5h2.99a12.5 12.5 0 0 0-.337-2.5zM4.51 8.5a12.5 12.5 0 0 0 .337 2.5H7.5V8.5zm3.99 0V11h2.653c.187-.765.306-1.608.338-2.5zM5.145 12c.138.386.295.744.468 1.068.552 1.035 1.218 1.65 1.887 1.855V12zm.182 2.472a7 7 0 0 1-.597-.933A9.3 9.3 0 0 1 4.09 12H2.255a7 7 0 0 0 3.072 2.472M3.82 11a13.7 13.7 0 0 1-.312-2.5h-2.49c.062.89.291 1.733.656 2.5zm6.853 3.472A7 7 0 0 0 13.745 12H11.91a9.3 9.3 0 0 1-.64 1.539 7 7 0 0 1-.597.933M8.5 12v2.923c.67-.204 1.335-.82 1.887-1.855.173-.324.33-.682.468-1.068zm3.68-1h2.146c.365-.767.594-1.61.656-2.5h-2.49a13.7 13.7 0 0 1-.312 2.5m2.802-3.5a7 7 0 0 0-.656-2.5H12.18c.174.782.282 1.623.312 2.5zM11.27 2.461c.247.464.462.98.64 1.539h1.835a7 7 0 0 0-3.072-2.472q.337.435.597.933M10.855 4a8 8 0 0 0-.468-1.068C9.835 1.897 9.17 1.282 8.5 1.077V4z"/></svg>
              </span>
              <span v-if="proof.docTypesUsed && proof.docTypesUsed.indexOf('image') >= 0" class="file-pill" :style="proofStatusColors[proof.id] ? proofStatusColors[proof.id].color : ''" title="Image">
                <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0"/><path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1z"/></svg>
              </span>
              <span v-if="proof.docTypesUsed && proof.docTypesUsed.indexOf('document') >= 0" class="file-pill" :style="proofStatusColors[proof.id] ? proofStatusColors[proof.id].color : ''" title="Document">
                <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2z"/></svg>
              </span>
              <span v-if="proof.docTypesUsed && proof.docTypesUsed.indexOf('video') >= 0" class="file-pill" :style="proofStatusColors[proof.id] ? proofStatusColors[proof.id].color : ''" title="Video">
                <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M0 1a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1zm4 0v6h8V1zm8 7H4v7h8zM1 1v2h2V1zm2 3H1v2h2zM1 7v2h2V7zm2 3H1v2h2zm-2 3v2h2v-2zM15 1h-2v2h2zm-2 3v2h2V4zm2 3h-2v2h2zm-2 3v2h2v-2zm2 3h-2v2h2z"/></svg>
              </span>
              <span v-if="proof.docTypesUsed && proof.docTypesUsed.indexOf('audio') >= 0" class="file-pill" :style="proofStatusColors[proof.id] ? proofStatusColors[proof.id].color : ''" title="Audio">
                <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M6 13c0 1.105-1.12 2-2.5 2S1 14.105 1 13s1.12-2 2.5-2 2.5.895 2.5 2m9-2c0 1.105-1.12 2-2.5 2s-2.5-.895-2.5-2 1.12-2 2.5-2 2.5.895 2.5 2"/><path fill-rule="evenodd" d="M14 11V2h1v9zM6 3v10H5V3z"/><path d="M5 2.905a1 1 0 0 1 .9-.995l8-.8a1 1 0 0 1 1.1.995V3L5 4z"/></svg>
              </span>
            </div>

            <div class="proof-actions" @click.stop>
              <a class="view-link" :href="proofItemLink(proof)" @click="handleViewProof($event, proof)">View</a>
              <button
                type="button"
                class="chevron-btn"
                :class="{ 'is-open': isExpanded(proof.id) }"
                :aria-expanded="isExpanded(proof.id) ? 'true' : 'false'"
                :aria-label="isExpanded(proof.id) ? 'Collapse details' : 'Expand details'"
                @click="toggleExpand(proof.id)"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            </div>
          </div>

          <div v-show="isExpanded(proof.id)" class="proof-row-accordion">
            <div class="accordion-grid">
              <div class="accordion-cell">
                <div class="accordion-label">Proof ID</div>
                <div class="accordion-value">{{ proof.id }}</div>
              </div>
              <div class="accordion-cell">
                <div class="accordion-label">Date Created</div>
                <div class="accordion-value">{{ formatDate(proof.createdAt) }}</div>
              </div>
              <div class="accordion-cell">
                <div class="accordion-label">Owner</div>
                <div class="accordion-value">{{ proof.currentSender || "—" }}</div>
              </div>
              <div class="accordion-cell">
                <div class="accordion-label">Due Date</div>
                <div class="accordion-value">{{ formatDate(proof.dueDate) }}</div>
              </div>
              <div class="accordion-cell">
                <div class="accordion-label">Current Stage</div>
                <div class="accordion-value">{{ proof.currentStageName || "—" }}</div>
              </div>
              <div class="accordion-cell">
                <div class="accordion-label">Waiting On</div>
                <div class="accordion-value">
                  {{ (isAwaitingDecision(proof) && proof.waitingOn && proof.waitingOn.length) ? proof.waitingOn.join(", ") : "—" }}
                </div>
              </div>
              <div
                v-for="customField in (proof.customFields || [])"
                :key="customField.accountProofPropertyLabel"
                class="accordion-cell"
              >
                <div class="accordion-label">{{ customField.accountProofPropertyLabel }}</div>
                <div class="accordion-value" v-if="customField.accountProofPropertyDataType === 'Datepicker'">
                  {{ formatDateProp(customField) }}
                </div>
                <div class="accordion-value" v-else-if="customField.accountProofPropertyDataType === 'Checkboxes (Multiple Options)'">
                  <div v-if="customField.proofPropertyValue && customField.proofPropertyValue.length">
                    <div v-for="(v, i) in customField.proofPropertyValue" :key="i">{{ v }}</div>
                  </div>
                  <div v-else>N/A</div>
                </div>
                <div class="accordion-value" v-else>
                  {{ (customField.proofPropertyValue && customField.proofPropertyValue.length > 0) ? customField.proofPropertyValue[0] : "N/A" }}
                </div>
              </div>
            </div>

            <div class="accordion-actions">
              <button v-if="!proof.archived" class="ghost-btn ghost-danger" @click="openSingleArchive(proof)">Archive</button>
              <button v-if="proof.archived" class="ghost-btn" @click="openSingleRestore(proof)">Restore</button>
              <button v-if="proof.archived && canAdminAccount" class="ghost-btn ghost-danger" @click="openSingleDelete(proof)">Delete</button>
            </div>
          </div>
        </li>
      </ul>

      <div v-else-if="hasActiveFilters || appliedSearch" class="noresults-wrap">
        <div class="noresults">
          <div class="noresults-scope" aria-hidden="true">
            <span class="scope-pulse scope-pulse-1"></span>
            <span class="scope-pulse scope-pulse-2"></span>
            <span class="scope-pulse scope-pulse-3"></span>
            <svg class="scope-lens" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="34" cy="34" r="22" stroke="#019256" stroke-width="2.5" />
              <circle cx="34" cy="34" r="14" stroke="#019256" stroke-opacity="0.4" stroke-width="1" stroke-dasharray="2 3" />
              <line x1="34" y1="20" x2="34" y2="48" stroke="#019256" stroke-opacity="0.35" stroke-width="1" />
              <line x1="20" y1="34" x2="48" y2="34" stroke="#019256" stroke-opacity="0.35" stroke-width="1" />
              <line x1="50" y1="50" x2="66" y2="66" stroke="#019256" stroke-width="3" stroke-linecap="round" />
              <circle cx="34" cy="34" r="2.5" fill="#019256" />
            </svg>
          </div>

          <div class="noresults-copy">
            <h2 class="noresults-title">{{ noResultsHeadline }}</h2>
            <p class="noresults-sub">{{ noResultsSub }}</p>
          </div>

          <div class="noresults-actions">
            <button
              v-if="hasAppliedSearch"
              type="button"
              class="noresults-action"
              @click="clearSearchOnly"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <line x1="20" y1="20" x2="16" y2="16" />
                <line x1="8" y1="8" x2="14" y2="14" />
                <line x1="14" y1="8" x2="8" y2="14" />
              </svg>
              Clear search
            </button>
            <button
              v-if="hasAppliedFilterChips"
              type="button"
              class="noresults-action"
              @click="clearFiltersOnly"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              Clear filters
            </button>
            <button
              v-if="hasAppliedSearch && hasAppliedFilterChips"
              type="button"
              class="noresults-action noresults-action-primary"
              @click="clearAllApplied"
            >
              Reset everything
            </button>
          </div>
        </div>
      </div>

      <div v-else class="empty-wrap">
        <div class="empty-card">
          <div class="empty-hero">
            <div class="empty-illustration" aria-hidden="true">
              <div class="illu-upload">
                <svg class="illu-upload-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <div class="illu-upload-bar"></div>
                <div class="illu-upload-bar short"></div>
              </div>
              <svg class="illu-arrow" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
              <div class="illu-stack">
                <div class="illu-card illu-card-3"></div>
                <div class="illu-card illu-card-2"></div>
                <div class="illu-card illu-card-1">
                  <div class="illu-paper-line"></div>
                  <div class="illu-paper-line short"></div>
                  <div class="illu-paper-pill"></div>
                </div>
              </div>
            </div>
          </div>

          <div class="empty-headline">
            <h2>Send your first proof for approval</h2>
            <p>Upload files, pick approvers, and collect clear feedback on every revision. Every proof you create lives right here.</p>
          </div>

          <ol class="empty-steps">
            <li>
              <span class="step-num">1</span>
              <div class="step-body">
                <div class="step-title">Upload your work</div>
                <div class="step-help">Images, PDFs, web pages, video, or audio — proofs support them all.</div>
              </div>
            </li>
            <li>
              <span class="step-num">2</span>
              <div class="step-body">
                <div class="step-title">Choose approvers</div>
                <div class="step-help">Pick contacts to review. Send a workflow with multiple stages if needed.</div>
              </div>
            </li>
            <li>
              <span class="step-num">3</span>
              <div class="step-body">
                <div class="step-title">Track every revision</div>
                <div class="step-help">Comments, statuses, and approvals land here as your reviewers respond.</div>
              </div>
            </li>
          </ol>

          <div class="empty-actions">
            <router-link to="/create" class="primary-btn empty-primary">Create a proof</router-link>
          </div>
        </div>
      </div>

      <div v-if="!isLoading && proofs.length && hasMore" ref="loadMoreSentinel" class="load-more-sentinel">
        <span v-if="isLoadingMore" class="load-more-spinner" aria-hidden="true"></span>
        <span class="load-more-text">
          {{ isLoadingMore ? "Loading more…" : `${proofs.length.toLocaleString()} loaded` }}
        </span>
      </div>
          </div>
    </div>
      </section>
    </div>
    </div>


    <transition name="bulk-bar">
      <div v-if="hasSelection" class="bulk-bar" role="region" aria-label="Bulk actions">
        <div class="bulk-bar-inner">
          <div class="bulk-count">
            <strong class="bulk-count-num">{{ selectedIds.length }}</strong>
            <span class="bulk-count-label">{{ bulkCountWord }} selected</span>
          </div>
          <button v-if="canSelectAllPage" type="button" class="bulk-promote-btn" @click="selectAllOnPage">
            Select all
          </button>
          <div class="bulk-actions">
            <button class="text-btn bulk-clear" @click="clearSelection" :disabled="isBulkBusy">Clear</button>
            <button v-if="!isArchivedView" class="ghost-btn bulk-action-btn" :disabled="isBulkBusy" @click="openBulkArchive">
              Archive
            </button>
            <button v-if="isArchivedView" class="ghost-btn bulk-action-btn" :disabled="isBulkBusy" @click="openBulkRestore">
              Restore
            </button>
            <button v-if="isArchivedView && canManage" class="primary-btn bulk-delete-btn" :disabled="isBulkBusy" @click="openBulkDelete">
              Delete
            </button>
          </div>
        </div>
      </div>
    </transition>

    <archiveProofModal
      name="archiveProofModal"
      :proofId="pendingArchive ? pendingArchive.id : undefined"
      @confirmed="onArchiveConfirmed"
    />
    <modal
      name="restoreProofModal"
      question="Restore selected proof(s)?"
      body="Restoring will allow you to actively manage these proofs, including new versions, approvers and workflows."
      confirm-button-text="Yes, Restore"
      @confirmed="onRestoreConfirmed"
    />
    <modal
      name="deleteProofModal"
      question="Delete selected proof(s)?"
      body="Are you sure you want to delete these proof(s)? You cannot undo this action."
      confirm-button-text="Yes, Delete"
      @confirmed="onDeleteConfirmed"
    />
  </div>
</template>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.experience-switch {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  column-gap: 16px;
  row-gap: 4px;
  padding: 8px 48px;
  background: rgba(1, 146, 86, 0.06);
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  font-size: 13px;
  color: #4b5563;
  flex-shrink: 0;
}
.experience-msg,
.experience-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.experience-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: #019256;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  letter-spacing: 0.02em;
}
.experience-text {
  color: #4b5563;
}
.experience-link {
  color: #019256;
  font-weight: 600;
  text-decoration: none;
}
.experience-link:hover {
  text-decoration: underline;
  color: #017a48;
}
.experience-link-btn {
  background: transparent;
  border: 0;
  padding: 0;
  font: inherit;
  cursor: pointer;
}
.experience-divider {
  color: #c2c8d0;
  font-weight: 600;
}
.experience-close {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.experience-close:hover {
  background: rgba(15, 23, 42, 0.06);
  color: #111827;
}

/* Wraps banner + shell so flex can resolve. Definite height (not
   min-height) is required so the shell's flex:1 yields a real pixel
   value that the sidebar can stretch to. */
.page-body {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px); /* clear position:fixed nav */
}
@media (max-width: 991px) {
  .page-body {
    height: auto;
    min-height: 100vh;
  }
}

/* Shell layout: sidebar + main column */
.proof-shell {
  display: flex;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  background: #fff;
  position: relative;
}

/* --- Filter rail (sidebar) --- */
.filter-rail {
  width: 288px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #fbfbfc;
  border-right: 1px solid rgba(15, 23, 42, 0.08);
  transition: width 220ms cubic-bezier(0.4, 0, 0.2, 1),
              transform 220ms cubic-bezier(0.4, 0, 0.2, 1),
              opacity 160ms ease;
  overflow: hidden;
  position: relative;
}
.proof-shell.sidebar-collapsed .filter-rail {
  width: 0;
  border-right-width: 0;
  opacity: 0;
  pointer-events: none;
}

.rail-head {
  flex-shrink: 0;
  height: 57px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 14px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
.rail-head-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.01em;
  white-space: nowrap;
}
.rail-head-title svg { color: #019256; flex-shrink: 0; }
.rail-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 6px;
  background: #019256;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
}
.rail-collapse-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.rail-collapse-btn:hover { background: rgba(15, 23, 42, 0.06); color: #111827; }

.rail-search {
  flex-shrink: 0;
  position: relative;
  min-height: 56px;
  display: flex;
  align-items: center;
  padding: 0 14px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: #fbfbfc;
}
.rail-search-icon {
  position: absolute;
  left: 26px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.55;
  pointer-events: none;
  z-index: 1;
}
.rail-search-input {
  width: 100%;
  height: 34px;
  padding: 0 12px 0 34px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  color: #1f2937;
  background: #fff;
  outline: none;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
.rail-search-input:focus {
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}
.rail-search-input::placeholder { color: #9ca3af; }

.rail-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 14px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  scrollbar-width: thin;
}
.rail-scroll::-webkit-scrollbar { width: 8px; }
.rail-scroll::-webkit-scrollbar-thumb {
  background: rgba(15, 23, 42, 0.12);
  border-radius: 999px;
  border: 2px solid #fbfbfc;
}
.rail-scroll::-webkit-scrollbar-thumb:hover { background: rgba(15, 23, 42, 0.22); }

.rail-group { display: flex; flex-direction: column; gap: 6px; }
.rail-label {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin: 0;
}
.rail-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.rail-foot {
  flex-shrink: 0;
  padding: 12px 14px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fbfbfc;
}
.rail-clear-btn {
  width: 100%;
  background: transparent;
  color: #4b5563;
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease, border-color 120ms ease;
}
.rail-clear-btn:hover:not(:disabled) {
  background: rgba(1, 146, 86, 0.08);
  color: #019256;
}
.rail-clear-btn:disabled { color: #c2c8d0; cursor: not-allowed; }

/* Inline "expand filters" pill, shown in utility bar when rail is collapsed */
.rail-expand-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  background: transparent;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 999px;
  color: #019256;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease;
}
.rail-expand-inline svg { color: #019256; }
.rail-expand-inline:hover {
  background: rgba(1, 146, 86, 0.08);
  border-color: rgba(1, 146, 86, 0.35);
}
.rail-expand-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 5px;
  background: #019256;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
}

/* --- Main column --- */
.proof-main-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: #f4f5f7;
}

.utility-bar {
  flex-shrink: 0;
  height: 57px;
  display: flex;
  align-items: stretch;
  background: transparent;
}
.utility-bar > .col-inner {
  height: 100%;
  padding: 0 20px;
  background: #fff;
  border-left: 1px solid rgba(15, 23, 42, 0.06);
  border-right: 1px solid rgba(15, 23, 42, 0.06);
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
.utility-left,
.utility-right {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

/* Centered max-width container for body content */
.col-inner {
  width: 100%;
  max-width: 991px;
  margin: 0 auto;
}
.col-inner-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.content-view > .col-inner {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: #fff;
  border-left: 1px solid rgba(15, 23, 42, 0.06);
  border-right: 1px solid rgba(15, 23, 42, 0.06);
}

.count-cell {
  min-height: 36px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.view-toggle {
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  padding: 3px;
  background: #f3f4f6;
  border-radius: 8px;
  gap: 2px;
}
.view-toggle-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 0 12px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease, box-shadow 120ms ease;
}
.view-toggle-btn:hover:not(.is-active) {
  color: #111827;
}
.view-toggle-btn.is-active {
  background: #fff;
  color: #019256;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}
.view-toggle-btn:focus { outline: none; }
.view-toggle-btn:focus-visible {
  outline: 2px solid rgba(1, 146, 86, 0.45);
  outline-offset: -1px;
}
.fetching-dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: #019256;
  animation: fetching-pulse 900ms ease-in-out infinite;
}
@keyframes fetching-pulse {
  0%, 100% { opacity: 0.35; transform: scale(0.85); }
  50%      { opacity: 1;    transform: scale(1.15); }
}

.tertiary-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  background: transparent;
  border: 1px solid transparent;
  color: #4b5563;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 120ms ease, color 120ms ease;
}
.tertiary-btn:hover { background: #f3f4f6; color: #111827; }
.tertiary-btn:disabled { opacity: 0.6; cursor: progress; }

.filter-btn { position: relative; }
.filter-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #019256;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow: 0 0 0 2px #fff;
}

.export-wrap,
.sort-wrap { position: relative; }
.tertiary-btn.is-active-btn { color: #019256; }
.row-menu-item.is-selected { color: #019256; }
.row-menu-item svg { color: #019256; flex-shrink: 0; }
.row-menu-check-spacer { display: inline-block; width: 14px; flex-shrink: 0; }
.export-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 20;
  min-width: 200px;
  padding: 4px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14), 0 2px 6px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  animation: row-menu-pop 140ms cubic-bezier(0.16, 1, 0.3, 1);
  transform-origin: top right;
}
@keyframes row-menu-pop {
  from { opacity: 0; transform: translateY(-4px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
.row-menu-item {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #1f2937;
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.row-menu-item:hover { background: rgba(1, 146, 86, 0.08); color: #019256; }
.row-menu-item:disabled { opacity: 0.5; cursor: not-allowed; }

.export-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(1, 146, 86, 0.25);
  border-top-color: #019256;
  border-radius: 999px;
  animation: bulk-spin 700ms linear infinite;
}

/* Chip bar */
.chip-bar {
  flex-shrink: 0;
  display: flex;
  align-items: stretch;
  background: transparent;
  min-height: 56px;
}
.chip-bar > .col-inner {
  padding: 10px 20px;
  background: #fbfbfc;
  border-left: 1px solid rgba(15, 23, 42, 0.06);
  border-right: 1px solid rgba(15, 23, 42, 0.06);
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
.chip-scroll {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.active-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 6px 0 10px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #e1e4ea;
  font-size: 12px;
  color: #1f2937;
  box-shadow: 0 1px 1px rgba(15, 23, 42, 0.03);
}
.chip-label { color: #6b7280; font-weight: 500; }
.chip-value {
  color: #111827;
  font-weight: 600;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chip-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-left: 2px;
  background: transparent;
  border: 0;
  border-radius: 999px;
  color: #9ca3af;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.chip-close:hover { background: rgba(1, 146, 86, 0.12); color: #019256; }
.chip-clear-all {
  flex-shrink: 0;
  background: transparent;
  border: 0;
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
}
.chip-clear-all:hover { color: #019256; background: rgba(1, 146, 86, 0.08); }

.chip-bar-enter-active,
.chip-bar-leave-active { transition: opacity 140ms ease, transform 160ms ease; }
.chip-bar-enter-from,
.chip-bar-leave-to,
.chip-bar-enter,
.chip-bar-leave-to { opacity: 0; transform: translateY(-4px); }

/* Content */
.content-view {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: transparent;
}
.loading-state {
  padding: 24px 16px;
  color: #6b7280;
  font-size: 14px;
}

/* Proof rows */
.proof-rows {
  background: #fff;
}
.proof-row {
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  position: relative;
  transition: background-color 120ms ease;
}
.proof-row.is-selected {
  background: rgba(1, 146, 86, 0.06);
}
.proof-row.is-selected::before {
  opacity: 1;
}
.proof-row::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #019256;
  opacity: 0;
  transition: opacity 120ms ease;
}
.proof-row:hover {
  background: #f6f7f9;
}
.proof-row.is-selected:hover {
  background: rgba(1, 146, 86, 0.09);
}
.proof-row.is-expanded {
  background: #fbfbfc;
}

.proof-row-main {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 16px;
  cursor: pointer;
  min-width: 0;
}
.proof-main {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}
.proof-name {
  display: inline-block;
  max-width: 100%;
  font-size: 14px;
  font-weight: 400;
  color: #0f172a;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color 120ms ease;
}
.proof-name:hover { color: #019256; text-decoration: none; }
.proof-sub {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 12px;
  color: #6b7280;
}
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 11px;
  font-weight: 600;
}
.not-viewed-badge {
  background: rgba(217, 177, 22, 0.12);
  color: #7a6207;
}
.viewed-select { margin-top: 8px; }
.status-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 999px;
  flex-shrink: 0;
}
.version-text { font-size: 12px; }
.stage-text {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #6b7280;
  min-width: 0;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.stage-text svg {
  flex-shrink: 0;
  color: #9ca3af;
}
.stage-names {
  font-weight: 600;
  color: #4b5563;
}
/* On mobile keep the stage name but drop the pending-approver names;
   the full list stays available in the expanded accordion. */
@media (max-width: 768px) {
  .stage-waiting {
    display: none;
  }
}

.proof-avatars {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
}
.file-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 0 0 2px #fff;
}
.file-pill + .file-pill {
  margin-left: -7px;
}
.proof-row:hover .file-pill,
.proof-row.is-expanded .file-pill {
  box-shadow: 0 0 0 2px #fbfbfc;
}
.proof-row.is-selected .file-pill {
  box-shadow: 0 0 0 2px #f1f7f4;
}
.file-pill svg {
  width: 12px;
  height: 12px;
  display: block;
}

.proof-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.view-link {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #019256;
  background: transparent;
  border: 1px solid rgba(1, 146, 86, 0.25);
  border-radius: 6px;
  text-decoration: none;
  transition: background-color 120ms ease, border-color 120ms ease;
}
.view-link:hover {
  background: rgba(1, 146, 86, 0.08);
  border-color: rgba(1, 146, 86, 0.45);
  color: #017a48;
  text-decoration: none;
}
.chevron-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease, transform 200ms ease;
}
.chevron-btn:hover { background: #ebebed; color: #111827; }
.chevron-btn.is-open { transform: rotate(180deg); color: #111827; }

.row-check-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 4px;
  cursor: pointer;
  flex-shrink: 0;
}
.row-checkbox {
  appearance: none;
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border: 1.5px solid #d1d5db;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  position: relative;
  margin: 0;
  flex-shrink: 0;
  transition: background-color 120ms ease, border-color 120ms ease;
}
.row-checkbox:hover { border-color: #019256; }
.row-checkbox:checked { background: #019256; border-color: #019256; }
.row-checkbox:checked::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 1px;
  width: 5px;
  height: 9px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

/* Accordion */
.proof-row-accordion {
  padding: 4px 24px 20px 50px;
  background: inherit;
  border-top: 1px dashed rgba(15, 23, 42, 0.08);
}
.accordion-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 32px;
  padding-top: 14px;
}
.accordion-cell { min-width: 0; }
.accordion-label {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}
.accordion-value {
  font-size: 13px;
  color: #1f2937;
  word-break: break-word;
}
.accordion-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 16px;
}

/* Empty / load more */
.empty-wrap {
  padding: 56px 24px;
  display: flex;
  justify-content: center;
}
.empty-card {
  width: 100%;
  max-width: 640px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 40px 40px 32px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 12px 32px rgba(15, 23, 42, 0.05);
  position: relative;
  overflow: hidden;
}
.empty-card::before {
  content: "";
  position: absolute;
  inset: 0 0 auto 0;
  height: 120px;
  background: linear-gradient(180deg, rgba(1, 146, 86, 0.06) 0%, rgba(1, 146, 86, 0) 100%);
  pointer-events: none;
}
.empty-card-compact {
  max-width: 480px;
  padding: 40px;
  text-align: center;
  box-shadow: none;
}
.empty-card-compact::before { content: none; }

/* No-results state (search/filters drove zero matches) */
.noresults-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 56px 24px 80px;
  background:
    radial-gradient(circle at 50% 38%, rgba(1, 146, 86, 0.06) 0%, rgba(1, 146, 86, 0) 60%),
    #fff;
}
.noresults {
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.noresults-scope {
  position: relative;
  width: 144px;
  height: 144px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 28px;
}
.scope-lens {
  position: relative;
  z-index: 2;
  width: 80px;
  height: 80px;
  filter: drop-shadow(0 4px 12px rgba(1, 146, 86, 0.18));
}
.scope-pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100px;
  height: 100px;
  margin: -50px 0 0 -50px;
  border-radius: 999px;
  border: 1px solid rgba(1, 146, 86, 0.35);
  opacity: 0;
  animation: scope-ping 3.6s cubic-bezier(0.2, 0.6, 0.2, 1) infinite;
}
.scope-pulse-2 { animation-delay: 1.2s; }
.scope-pulse-3 { animation-delay: 2.4s; }
@keyframes scope-ping {
  0%   { transform: scale(0.55); opacity: 0; }
  20%  { opacity: 0.7; }
  100% { transform: scale(1.45); opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .scope-pulse { animation: none; opacity: 0.35; transform: scale(1); }
}

.noresults-copy { margin-bottom: 22px; }
.noresults-title {
  font-family: 'Open Sans', system-ui, -apple-system, sans-serif;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #0f172a;
  margin: 0 0 8px;
  line-height: 1.25;
}
.noresults-sub {
  font-size: 14px;
  line-height: 1.55;
  color: #6b7280;
  margin: 0 auto;
  max-width: 380px;
}

.noresults-actions {
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}
.noresults-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 14px;
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 999px;
  color: #1f2937;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease, box-shadow 120ms ease;
}
.noresults-action svg { color: #019256; }
.noresults-action:hover {
  background: rgba(1, 146, 86, 0.06);
  border-color: rgba(1, 146, 86, 0.32);
  color: #019256;
  box-shadow: 0 1px 2px rgba(1, 146, 86, 0.08);
}
.noresults-action-primary {
  background: #019256;
  border-color: #019256;
  color: #fff;
}
.noresults-action-primary:hover {
  background: #017a48;
  border-color: #017a48;
  color: #fff;
  box-shadow: 0 2px 8px rgba(1, 146, 86, 0.28);
}

.empty-hero {
  display: flex;
  justify-content: center;
  position: relative;
  margin-bottom: 28px;
}
.empty-illustration {
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;
  z-index: 1;
}
.illu-upload {
  width: 96px;
  height: 96px;
  background: #fff;
  border: 1px dashed #94a3b8;
  border-radius: 10px;
  padding: 14px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
}
.illu-upload-icon {
  color: #019256;
  opacity: 0.85;
}
.illu-upload-bar {
  height: 5px;
  border-radius: 3px;
  background: #e5e7eb;
  width: 70%;
}
.illu-upload-bar.short {
  width: 45%;
}
.illu-arrow {
  color: #019256;
  opacity: 0.6;
}
.illu-stack {
  position: relative;
  width: 96px;
  height: 96px;
}
.illu-card {
  position: absolute;
  width: 96px;
  height: 72px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e5e7eb;
}
.illu-card-1 {
  left: 0;
  top: 16px;
  z-index: 3;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-color: rgba(1, 146, 86, 0.4);
  box-shadow: 0 6px 16px rgba(1, 146, 86, 0.18);
}
.illu-card-2 {
  left: 6px;
  top: 8px;
  z-index: 2;
  background: #ecfdf5;
}
.illu-card-3 {
  left: 12px;
  top: 0;
  z-index: 1;
  background: #f3f4f6;
}
.illu-paper-line {
  height: 5px;
  background: #019256;
  border-radius: 3px;
  width: 100%;
  opacity: 0.65;
}
.illu-paper-line.short {
  width: 55%;
  opacity: 0.45;
}
.illu-paper-pill {
  margin-top: auto;
  width: 32px;
  height: 8px;
  border-radius: 999px;
  background: rgba(1, 146, 86, 0.18);
}

.empty-headline {
  text-align: center;
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
}
.empty-headline h2 {
  font-family: 'Open Sans', system-ui, -apple-system, sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 8px;
  letter-spacing: -0.01em;
}
.empty-headline p {
  font-size: 14px;
  line-height: 1.55;
  color: #6b7280;
  margin: 0 auto;
  max-width: 440px;
}
.empty-card-compact .empty-headline {
  margin-bottom: 0;
}

.empty-steps {
  list-style: none;
  padding: 20px;
  margin: 0 0 28px;
  background: #f9fafb;
  border: 1px solid #f1f3f5;
  border-radius: 10px;
  position: relative;
  z-index: 1;
}
.empty-steps li {
  display: flex;
  gap: 14px;
  padding: 10px 0;
  align-items: flex-start;
}
.empty-steps li + li {
  border-top: 1px solid #f1f3f5;
}
.step-num {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #d1d5db;
  color: #374151;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.step-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 2px;
}
.step-help {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.45;
}

.empty-actions {
  display: flex;
  position: relative;
  z-index: 1;
}
.empty-primary {
  width: 100%;
  justify-content: center;
  text-decoration: none;
}
.empty-primary:hover { color: #fff; text-decoration: none; }
.load-more-sentinel {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px 16px 28px;
  font-size: 12px;
  color: #6b7280;
}
.load-more-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(1, 146, 86, 0.25);
  border-top-color: #019256;
  border-radius: 999px;
  animation: bulk-spin 700ms linear infinite;
}

/* Form primitives shared by sidebar */
.manage-link {
  background: transparent;
  border: 0;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  color: #019256;
  cursor: pointer;
  text-transform: none;
  letter-spacing: 0;
}
.manage-link:hover { text-decoration: underline; }
.filter-input {
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #1f2937;
  background: #fff;
  outline: none;
  font-family: inherit;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
.filter-input:focus {
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}
.select-wrap { position: relative; }
.filter-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding-right: 32px;
  cursor: pointer;
}
.select-caret {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}
.overdue-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  cursor: pointer;
  align-self: flex-start;
}
.overdue-toggle input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #019256;
  cursor: pointer;
}
.overdue-label {
  font-size: 13px;
  color: #1f2937;
  font-weight: 500;
}
.check-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 16px;
  padding: 4px 0;
}
.check-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #1f2937;
  cursor: pointer;
}
.check-item input[type="checkbox"] {
  width: 14px;
  height: 14px;
}

.rail-group >>> .multiselect {
  min-height: 38px;
  font-size: 14px;
}
/* Match the selected single value + search input to the sibling filter inputs.
   vue-multiselect sets a larger font on these elements directly, which
   overrides the .multiselect cascade above and makes the value look oversized. */
.rail-group >>> .multiselect__single,
.rail-group >>> .multiselect__input {
  font-size: 14px;
  line-height: 24px;
  padding-left: 0;
  margin-bottom: 0;
  color: #1f2937;
  background: transparent;
}
.rail-group >>> .multiselect__single {
  display: block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rail-group >>> .multiselect__tags {
  min-height: 38px;
  padding: 6px 36px 0 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
}
.rail-group >>> .multiselect--active .multiselect__tags {
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}
.rail-group >>> .multiselect__tag {
  background: #019256;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  padding: 3px 22px 3px 8px;
  line-height: 18px;
}
.rail-group >>> .multiselect__tag-icon {
  width: 18px;
  line-height: 18px;
  border-radius: 0 6px 6px 0;
  font-weight: 400;
  transition: background-color 120ms ease;
}
.rail-group >>> .multiselect__tag-icon::after {
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
}
.rail-group >>> .multiselect__tag-icon:hover {
  background: rgba(0, 0, 0, 0.18);
}
.rail-group >>> .multiselect__tag-icon:hover::after {
  color: #fff;
}
.rail-group >>> .multiselect__option--highlight {
  background: rgba(1, 146, 86, 0.12);
  color: #111827;
}
.rail-group >>> .multiselect__option--selected.multiselect__option--highlight {
  background: #019256;
  color: #fff;
}

.primary-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #019256;
  color: #fff;
  border: 0;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(1, 146, 86, 0.2);
  text-decoration: none;
  transition: background-color 120ms ease, transform 80ms ease, box-shadow 120ms ease;
}
.primary-btn:hover { background: #017a48; box-shadow: 0 2px 6px rgba(1, 146, 86, 0.25); }
.primary-btn:active { transform: translateY(1px); }
.ghost-btn {
  background: #fff;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 9px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
}
.ghost-btn:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #9ca3af;
}
.ghost-btn:disabled { color: #9ca3af; cursor: not-allowed; opacity: 0.6; }
.ghost-danger { color: #b91c1c; border-color: rgba(185, 28, 28, 0.25); }
.ghost-danger:hover:not(:disabled) {
  background: rgba(185, 28, 28, 0.05);
  border-color: rgba(185, 28, 28, 0.5);
  color: #991b1b;
}
.text-btn {
  background: transparent;
  border: 0;
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: color 120ms ease, background-color 120ms ease;
}
.text-btn:hover { color: #111827; background: #f3f4f6; }

/* Bulk bar */
.bulk-bar {
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 1050;
  max-width: 92vw;
}
.bulk-bar-inner {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 10px 12px 10px 20px;
  background: #0f172a;
  color: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.35), 0 4px 10px rgba(15, 23, 42, 0.15);
}
.bulk-count { display: inline-flex; align-items: baseline; gap: 6px; font-size: 13px; }
.bulk-count-num { font-size: 16px; font-weight: 700; color: #fff; }
.bulk-count-label { color: rgba(255, 255, 255, 0.7); font-weight: 500; }
.bulk-promote-btn {
  background: transparent;
  border: 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  font-weight: 500;
  padding: 6px 4px;
  cursor: pointer;
  transition: color 120ms ease;
}
.bulk-promote-btn:hover { color: #fff; }
.bulk-actions { display: inline-flex; align-items: center; gap: 8px; margin-left: auto; }
.bulk-actions .text-btn { color: rgba(255, 255, 255, 0.7); }
.bulk-actions .text-btn:hover { color: #fff; background: rgba(255, 255, 255, 0.08); }
.bulk-action-btn {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.18);
}
.bulk-action-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.32);
  color: #fff;
}
.bulk-delete-btn {
  background: #dc2626;
  box-shadow: 0 1px 2px rgba(220, 38, 38, 0.25);
}
.bulk-delete-btn:hover:not(:disabled) {
  background: #b91c1c;
  box-shadow: 0 2px 6px rgba(220, 38, 38, 0.3);
}

.bulk-bar-enter-active,
.bulk-bar-leave-active { transition: opacity 160ms ease, transform 200ms cubic-bezier(0.16, 1, 0.3, 1); }
.bulk-bar-enter-from,
.bulk-bar-enter,
.bulk-bar-leave-to { opacity: 0; transform: translateX(-50%) translateY(16px); }

@keyframes bulk-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .proof-row-main { padding: 16px; }
  .proof-avatars { display: none; }
  .accordion-grid { grid-template-columns: 1fr; }
  .proof-row-accordion { padding-left: 24px; }
}
@media (max-width: 640px) {
  .experience-switch {
    justify-content: space-between;
    padding: 8px 14px;
    font-size: 12px;
    line-height: 1.35;
    text-align: left;
  }
  .experience-msg,
  .experience-actions {
    flex: 1 1 100%;
  }
  .experience-actions { justify-content: flex-end; }
}
@media (max-width: 380px) {
  .experience-actions {
    justify-content: flex-start;
    gap: 8px;
  }
  .experience-divider { display: none; }
}
@media (max-width: 720px) {
  /* On narrow screens, the rail overlays the content like a drawer */
  .filter-rail {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 12;
    box-shadow: 12px 0 32px rgba(15, 23, 42, 0.18);
  }
  .proof-shell.sidebar-collapsed .filter-rail {
    transform: translateX(-100%);
    width: 288px;
    border-right-width: 1px;
    opacity: 0;
  }
}
</style>
